<script type="text/jsx">
import _ from 'lodash';
import { isEmptyVal } from '@/utils';
import { BILLING_CYCLE, BillingCycleEnum } from '@/common/constants';
import { getPlanUnitWithCycle, planUnitMap } from '@/common/product-unit';
import SaveCount from '@/components/save-count';

export default {
  name: 'config-cycle',
  components: { SaveCount },
  model: {
    prop: 'billingCycle',
    event: 'syncCycle',
  },
  props: {
    // billing cycle plan list
    billingCycleList: {
      type: Array,
      required: true,
      default: () => [],
    },
    billingCycle: String,
    showCycleAlert: Boolean,
    unit: {
      type: Number,
      default: planUnitMap.license,
      validator: function(v) {
        return _.chain(planUnitMap)
        .values()
        .includes(v)
        .value();
      }
    },
    trackingPlanName: {
      type: String,
      required: false,
      default: '',
    }
  },
  computed: {
    currentPlanList() {
      return _.map(this.billingCycleList, (p) => p.plan);
    },
    groupByPlanListByCycle() {
      return _.groupBy(this.currentPlanList, (p) => p?.billing_cycle);
    },
    firstDisabledCycleAlertItem() {
      return _.find(this.billingCycleList, (p) => p.disabled && !isEmptyVal(p.alert));
    },
  },
  methods: {
    // FREE TRIAL - abstract single unit because for free trial, it should show 'unit + 'after trial''
    mx_renderCycleInnerVNode(item = {}) {
      let { plan = {}, disabledInnerDesc = '' } = item;
      return disabledInnerDesc ?
        <p class="opc-cycle__disabled--desc">{disabledInnerDesc}</p> :
        <p class="opc-cycle__plan__price">
              <span class="opc-cycle__plan__price--amount">
                {this.$money(plan.amount, { forceDiff: true })}
              </span>
          <span class="opc-cycle__plan__price--unit">
            {this.$t(plan.billing_cycle ? getPlanUnitWithCycle(plan.billing_cycle, this.unit)  : '')}
              </span>
        </p>;
    },
    mx_renderBillingCycleCol(item = {}) {
      let { plan = {}, disabled = false, tooltips = '', alert = "" } = item;

      const shortestCycle = _.sortBy(Object.keys(this.groupByPlanListByCycle), cycle => Object.keys(BillingCycleEnum).indexOf(cycle))[0];
      const shortestCyclePlan = _.get(this.groupByPlanListByCycle, [shortestCycle, 0]);
      const saveProps = plan.cycleValue >= BillingCycleEnum[BILLING_CYCLE.QUARTERLY] ? {
        disabled,
        monthlyPrice: shortestCyclePlan?.amount / shortestCyclePlan?.cycleValue,
        annualPrice: (plan.amount / plan.cycleValue) * 12,
      } : {};

      const planIsSelected = plan?.cartInfo?.selected_number > 0;

      // support current plan have been selected, but the current have been disabled case
      if (planIsSelected) disabled = false;

      const cycleRadioVNode = <zm-radio
        disabled={disabled}
        label={plan.billing_cycle}
      >
        <p class="opc-cycle__plan__title">
          {this.$t(plan.billing_cycle ? `billing.billing_cycle.${plan.billing_cycle}` : '')}
          <save-count
            class="opc-cycle__plan__save"
            {...{ props: saveProps }}
          />
        </p>
        {this.mx_renderCycleInnerVNode(item)}
      </zm-radio>;

      const shouldShowTips = (this.showCycleAlert && isEmptyVal(alert) && !isEmptyVal(tooltips))
        || (!isEmptyVal(tooltips) && !this.showCycleAlert);

      if (shouldShowTips) {
        return <zm-tooltip key={plan.plan_code}>
          <p slot="content" domPropsInnerHTML={tooltips}></p>
          {cycleRadioVNode}
        </zm-tooltip>;
      }

      return cycleRadioVNode;
    },
    mx_renderAlert(slot, title) {
      if (slot) return slot;
      if (title) return <zm-alert
        class="opc-common-info-alert"
        type="warning"
        closable={false}
        show-icon>
        <p domPropsInnerHTML={title} onClick={this.trackingAlert} />
      </zm-alert>;
    },
    mx_renderCycleDisabledAlert() {
      if (!this.showCycleAlert || !this.firstDisabledCycleAlertItem) return null;
      return this.mx_renderAlert(this.$slots?.cycleAlert, this.firstDisabledCycleAlertItem?.alert);
    },
    switchBillingCycle(oldPlan, newPlan) {
      this.$emit("switchCycleAction", oldPlan, newPlan);
    },
    changeBillingCycle(cycle) {
      this.$emit("syncCycle", cycle);
    },
    trackingAlert(e) {
      const { tagName = "", className, text = "" } = e?.target || {};
      const { alert } = this.firstDisabledCycleAlertItem || {};

      if (
        tagName?.toLowerCase() === "a"
        && className === "opc-go-back__billing"
        && this.showCycleAlert
        && alert
      ) {
        this.$tracking.buyInteraction(
          `banner - product level`,
          `${this.trackingPlanName ? `${this.trackingPlanName} - `: ''}${alert} - ${text}`,
        );
      }
    },
  },
  render() {
    const classList = ['opc-card-radio', 'opc-cycle__plan', `opc-cycle__plan__${this.currentPlanList?.length || 2}`];

    return <div class="opc-cycle">
      <p id="opc-cycle__title" class="opc-cycle__title">{this.$t('billing.select_billing_cycle')}</p>
      <cycle-radio-group
        enter-select
        value={this.billingCycle}
        onInput={this.changeBillingCycle}
        plans={this.currentPlanList}
        action={this.switchBillingCycle}
        aria-label="Select a subscription cycle"
        class={classList}>
        {_.map(this.billingCycleList, (p) => this.mx_renderBillingCycleCol(p))}
      </cycle-radio-group>
      {this.mx_renderCycleDisabledAlert()}
    </div>;
  },
  watch: {
    firstDisabledCycleAlertItem: {
      immediate: true,
      handler(item) {
        if (this.showCycleAlert && item?.alert) {
          this.$tracking.buyInteraction(
            'banner - product level',
            `${this.trackingPlanName ? `${this.trackingPlanName} - `: ''}${item.alert} - impression`,
          );
        }
      },
    }
  }
};
</script>

<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';

@import '@/styles/scss-utils.scss';
.opc-cycle {
  margin-top: 16px;

  &__title {
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 18px;
    line-height: 150%;
    color: #00053D;
    height: 100%;
    @include darkTextColor;
  }

  &__plan {
    margin-top: 8px;

    &__2.opc-card-radio {
      gap: 16px;
    }

    &__3.opc-card-radio {
      gap: 12px;
    }

    .zm-radio {
      flex: 1;
      min-width: 0;

      &__wrap {
        width: 100%;
      }

      &__label {
        width: 100%;

        &--inner {
          width: 100%;
          /* padding-right: 2px !important; */
        }

        &.is-disabled {
          .opc-cycle__plan__price--amount,
          .opc-cycle__plan__price--unit {
            color: #9191A4;
            @include darkTextDisable;
          }
        }
      }
    }

    &__title {
      font-weight: 700;
      font-size: 16px;
      text-transform: capitalize;
      display: flex;
      align-items: center;
      flex-wrap: wrap; // for i18n
    }

    &__price {
      display: flex;
      flex-wrap: wrap;
      align-items: baseline;

      &--amount, &--unit {
        color: #00053D;
        line-height: 150%;
        @include darkTextColor;
      }

      &--amount {
        font-weight: 700;
        font-size: 22px;
        word-break: break-all;
      }

      &--unit {
        font-weight: 500;
        font-size: 12px;
        color: #515079;
        word-break: break-all;
        @include darkTextColor2;
      }
    }
  }

  &__disabled--desc {
    font-family: Almaden Sans;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    letter-spacing: 0em;
    text-align: left;
    color: #515079;
    margin-top: 8px;
    @include darkTextDisable;
  }

  &__plan__save {
    margin-left: 4px;
  }
}

@include media(mobile) {
  .opc-cycle {
    margin-top: 16px;
    &__title {
      font-size: 18px;
    }
    &__plan {
      margin-top: 8px;
      &__2.opc-card-radio {
        gap: 16px;
      }
      &__3.opc-card-radio {
        gap: 12px;
      }
      &__title {
        font-size: 16px;
      }
      &__price {
        &--amount {
          font-size: 22px;
        }
        &--unit {
          font-size: 12px;
        }
      }
    }

    &__disabled--desc {
      font-size: 14px;
      line-height: 21px;
      margin-top: 8px;
    }
    &__plan__save {
      margin-left: 4px;
    }
  }
}
</style>
